import { useConditionalBackground } from "@/contexts/useConditionalBackground";
import { BrlCurrencyFormatter } from "@/utils/BrlCurrencyFormatter";
import {
  Badge,
  Box,
  HStack,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useColorModeValue,
  Collapse,
  IconButton,
  Flex,
} from "@chakra-ui/react";
import { Fragment, useMemo, useState } from "react";
import { AiOutlineEye } from "react-icons/ai";
import { ChevronUpIcon } from "@chakra-ui/icons";
import { ProposalSidebarItem } from "../ConciliationSidebar";
import ConciliationDetails from "../ConciliationDetails";
import Pagination from "@/components/FinancialShared/Pagination";
import { ConfiguracaoConciliacao } from "@/models/conciliation-config.model";
import { ConciliacaoStatus } from "@/models/consignado/propostaFinanceira.model";

interface ConcignadoListViewProps {
  proposals: ProposalSidebarItem[];
  totalItems: number;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number, pageSize: number) => void;
  onConciliationUpdated: () => Promise<void> | void;
  isLoading?: boolean;
  configuracoes: ConfiguracaoConciliacao[];
}

const baseProposalHeaders = [
  "AÇÕES",
  "SHORTHASH",
  "CCB",
  "DATA DA PROPOSTA",
  "VALOR LIBERADO",
];

const normalizeConciliationStepName = (value: string) => value.trim().toLocaleLowerCase("pt-BR");

const getStatusMeta = (status: ConciliacaoStatus) => {
  if (status === ConciliacaoStatus.DIVERGENTE) {
    return {
      colorScheme: "red",
      label: "Divergente",
    };
  }

  if (status === ConciliacaoStatus.CONCLUIDO) {
    return {
      colorScheme: "green",
      label: "Concluído",
    };
  }

  if (status === ConciliacaoStatus.NAO_SE_APLICA) {
    return {
      colorScheme: "gray",
      label: "Não se aplica",
    };
  }

  return {
    colorScheme: "yellow",
    label: "Pendente",
  };
};

export default function ConsignadoListView({ proposals, totalItems, currentPage, pageSize, onPageChange, onConciliationUpdated, isLoading, configuracoes }: ConcignadoListViewProps) {
  const backgroundStyle = useConditionalBackground();
  const headerColor = useColorModeValue("white", "white");
  const rowHoverBg = useColorModeValue("teal.50", "teal.900");
  const rowBorderColor = useColorModeValue("gray.200", "gray.600");
  const [selectedRow, setSelectedRow] = useState<string>('');
  const totalPages = Math.ceil(totalItems / pageSize) || 1;

  const sortedConfiguracoes = useMemo(() => {
    return [...configuracoes].sort((a, b) => a.ordem - b.ordem);
  }, [configuracoes]);

  const proposalHeaders = useMemo(() => {
    return [...baseProposalHeaders, ...sortedConfiguracoes.map((configuracao) => configuracao.nome)];
  }, [sortedConfiguracoes]);

  const totalColumns = proposalHeaders.length;

  const toggleRow = (proposalId: string) => {
    setSelectedRow((prev) => prev === proposalId ? '' : proposalId);
  };


  const renderCurrency = (value?: number) => {
    if (typeof value !== "number" || Number.isNaN(value))
      return "-";

    return BrlCurrencyFormatter(value);
  };

  const renderEtapaStatus = (proposal: ProposalSidebarItem, etapaNome: string) => {
    const etapa = proposal.etapasConciliacao?.find((item) => {
      return normalizeConciliationStepName(item.etapa) === normalizeConciliationStepName(etapaNome);
    });

    if (!etapa)
      return "-";

    const statusMeta = getStatusMeta(etapa.status);

    return (
      <Badge colorScheme={statusMeta.colorScheme} variant="subtle">
        {statusMeta.label}
      </Badge>
    );
  };

  return (
  <Box w="100%" minW="0">
    <Box overflow="auto" borderRadius="md" boxShadow="md">
      <Table w={"100%"} size="sm">
        <Thead {...backgroundStyle} boxShadow="md">
          <Tr>
            {proposalHeaders.map((thead) => (
              <Th
                w="120px"
                color={headerColor}
                textAlign="center"
                fontSize="xs"
                py={5}
                px={2}
                key={thead}
              >
                <HStack justify="center">
                  <Text>{thead}</Text>
                </HStack>
              </Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {isLoading ? (
            <Tr>
              <Td colSpan={totalColumns} textAlign="center" py={8}>
                <Flex justifyContent="center" alignItems="center" gap={2}>
                  <Spinner size="sm" />
                  <Text color="gray.500">Carregando propostas...</Text>
                </Flex>
              </Td>
            </Tr>
          ) : proposals.length === 0 ? (
            <Tr>
              <Td colSpan={totalColumns} textAlign="center" py={8}>
                <Text color="gray.500">Nenhuma proposta encontrada</Text>
              </Td>
            </Tr>
          ) : (
            <>
              {proposals.map((proposal) => {
                const isExpanded = proposal.id === selectedRow

                return (
                  <Fragment key={proposal.id}>
                    <Tr
                      _hover={{ bg: rowHoverBg }}
                      borderBottom="1px solid"
                      borderColor={rowBorderColor}
                      transition="all 0.3s ease-in-out"
                      onClick={() => toggleRow(proposal.id)}
                      cursor='pointer'
                    >
                      <Td textAlign="center" fontSize="sm" py={3}>
                        <HStack justify="center" spacing={2}>
                          <Tooltip label={isExpanded ? "Ocultar Detalhes" : "Ver Detalhes"} placement="top">
                            <IconButton
                              aria-label="Toggle details"
                              icon={isExpanded ? <ChevronUpIcon boxSize={6} /> : <AiOutlineEye fontSize='1.25rem' />}
                              size="sm"
                              variant="ghost"
                              colorScheme="teal"
                              onClick={(e) => {
                                e.stopPropagation(); 
                                toggleRow(proposal.id)}
                              }
                            />
                          </Tooltip>
                        </HStack>
                      </Td>
                      <Td textAlign="center" fontSize="sm" py={3}>
                        {proposal?.shortHash ? proposal.shortHash : "-"}
                      </Td>
                      <Td textAlign="center" fontSize="sm" py={3}>
                        {proposal.numeroCcb || "-"}
                      </Td>
                      <Td textAlign="center" fontSize="sm" py={3}>
                        <Box>
                          {proposal.dataCriacaoProposta ? new Date(proposal.dataCriacaoProposta).toLocaleDateString("pt-BR") : "-"}
                        </Box>
                        <Box fontSize="xs" color="gray.500">
                          {proposal.dataCriacaoProposta ? new Date(proposal.dataCriacaoProposta).toLocaleTimeString("pt-BR", {
                            hour: "2-digit",
                            minute: "2-digit",
                          }) : "-"}
                        </Box>
                      </Td>
                      <Td textAlign="center" fontSize="sm" py={3}>
                        {renderCurrency(proposal.valorLiberado)}
                      </Td>

                      {sortedConfiguracoes.map((configuracao) => (
                        <Td
                          key={`${proposal.id}-${configuracao.id}`}
                          textAlign="center"
                          fontSize="sm"
                          py={3}
                        >
                          {renderEtapaStatus(proposal, configuracao.nome)}
                        </Td>
                      ))}

                    </Tr>

                    {isExpanded && (
                      <Tr>
                        <Td colSpan={totalColumns} p={0} borderBottom="1px solid" borderColor={rowBorderColor}>
                          <Collapse in={isExpanded} animateOpacity>
                            <Box
                              bg={useColorModeValue("gray.50", "gray.700")}
                              p={6}
                              w="100%"
                            >
                              <ConciliationDetails
                                key={proposal.id}
                                proposalId={proposal.id}
                                isShowSteps={true}
                                configuracoes={configuracoes}
                                onConciliationUpdated={onConciliationUpdated}
                              />
                            </Box>
                          </Collapse>
                        </Td>
                      </Tr>
                    )}
                  </Fragment>
                );
              })}
            </>
          )}
        </Tbody>
      </Table>
      </Box>
      
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        pageSize={pageSize}
        isQuerying={Boolean(isLoading)}
        onPageChange={(page) => onPageChange(page, pageSize)}
        onPageSizeChange={(size) => onPageChange(1, size)}
      />
    </Box>
  );
}
